/**
 * Who owns a drone, relative to the operator looking at it (#385, #575).
 *
 * Not a property of the aircraft — a property of the aircraft *and the viewer*,
 * which is why it comes from the server. `GET /api/v1/drones` answers in three
 * buckets and this is which one a drone arrived in.
 *
 * `others` exists because `mine` used to mean "has an owner". That is true for
 * an ordinary operator, whose response only ever contains their own drones plus
 * the unclaimed ones — and false for an admin, who holds `fleet:read_all` and
 * receives everybody's. The fleet view told an admin that two of another
 * operator's drones were theirs (#575).
 */
export type DroneOwnership = 'mine' | 'others' | 'unclaimed';

/**
 * Which drones the fleet view shows (#385).
 *
 * The three ownership values partition `all`, so the counts add up whoever is
 * asking. Lives here rather than in the store slice because it is also a
 * persisted user preference, and a preferences document should not import from
 * a reducer.
 */
export type OwnershipFilter = 'all' | DroneOwnership;

/** The selectable fleet views, in the order the filter offers them. */
export const OWNERSHIP_FILTERS: readonly OwnershipFilter[] = [
  'all',
  'mine',
  'others',
  'unclaimed',
] as const;

/**
 * What to call a drone on screen: its name, or its ID when nobody has named it
 * (G2). The one place that fallback is written, so every view agrees on it.
 */
export const droneLabel = (drone: Pick<Drone, 'id' | 'name'>): string =>
  drone.name ?? drone.id;

/** The longest drone name the API accepts, in characters (G2). */
export const MAX_DRONE_NAME_LENGTH = 64;

export interface Drone {
  id: string;
  /**
   * Who owns this drone, relative to the signed-in operator (#385, #575).
   *
   * Derived from which bucket of `GET /api/v1/drones` it arrived in, not from a
   * field on the drone itself — the API answers with three arrays and this is
   * the one place that distinction crosses into the UI. The server is the
   * authority on ownership and the only party that knows both sides of the
   * comparison; the alternative was to ship `owner_id` here and match it
   * against `/me`, which is a second implementation of ownership free to drift
   * from the one the API enforces.
   *
   * Optional so a drone built in a test or arriving over the WebSocket reads as
   * `mine` — the reading that shows it as ordinary rather than flagging it.
   */
  ownership?: DroneOwnership;
  /**
   * What people call this drone, or absent when nobody has named it (#406).
   *
   * Absent rather than filled with the ID: the rename control has to know
   * there is no name to start from, and a fallback stored here would be
   * indistinguishable from a pilot who typed the ID. Show it with
   * {@link droneLabel}, which falls back to the ID.
   *
   * `callSign` used to sit beside this, filled with the ID, and nothing on the
   * server ever backed it (G2). The name replaces it.
   */
  name?: string;
  /** The airframe's serial, as the card shows it (G7). Absent from an older backend. */
  hardwareId?: string;
  /**
   * The model as the drone was enrolled with it, free text until the catalog
   * arrives (#407). Absent when nobody said.
   */
  model?: string;
  /**
   * The build of the software the drone's board runs, as it last reported it
   * (#791). Absent until a board new enough to say has reported.
   */
  softwareVersion?: string;
  /** A simulator rather than an aircraft (#322): it has no registration record. */
  virtual?: boolean;
  /**
   * The state of the drone's refresh token (#777, C16), never the token. Absent
   * for a drone never issued one, and from an older backend.
   */
  credentials?: DroneCredentials;
  /**
   * `link_lost` is a drone that went silent in the air (#787): its last
   * position is where it was, not where it is. Silent on the ground is
   * `offline`. `shut_down_in_flight` signed off in the air (#799): its
   * controller stopped above the ground, and the autopilot's failsafe has it.
   */
  status:
    | 'idle'
    | 'in_flight'
    | 'returning'
    | 'landed'
    | 'error'
    | 'offline'
    | 'link_lost'
    | 'shut_down_in_flight';
  /**
   * Where the drone last reported itself, or `undefined` when it never has
   * (#508).
   *
   * Optional because "no known position" is a real state, not a gap to fill.
   * A drone registered through the factory flow, or one that has not reported
   * since this page loaded, genuinely has no coordinates — and the honest
   * rendering is to leave it off the map and say so, not to place it at 0,0 in
   * the Gulf of Guinea.
   *
   * This field was required until #508, and `GET /api/v1/drones` has never
   * populated it: the API sends `last_known_position`, omitted entirely when the
   * drone has no position row. Every consumer dereferenced it anyway, so the
   * fleet map crashed as soon as the list contained a drone without telemetry.
   */
  position?: {
    latitude: number;
    longitude: number;
    altitude: number;
  };
  /**
   * Flight commands this drone's current state admits, as the server computed
   * them (#578).
   *
   * Not derived here on purpose. Which state allows which command has already
   * drifted once between the API and the aircraft, in both directions at once
   * (#556); a browser keeping its own copy would make a third. The panel
   * renders what this says and decides nothing.
   *
   * Optional, and absent is the common case rather than an edge: a drone
   * arriving over the WebSocket carries no ruling, and neither does one from a
   * backend older than #578. Absent means no flight controls — offering them
   * for an aircraft whose state we are guessing at is exactly the wrong
   * default, and declaring a field required that the server does not always
   * send is how #508 crashed the map.
   */
  availableCommands?: string[];
  /**
   * Where the nose points, in degrees from north, 0 to 359.99 (#711): the
   * aircraft's compass and AHRS, not its direction of travel. In a crosswind
   * the two differ, and this is the nose.
   *
   * Absent when the aircraft has not reported one. Absent is not north, and
   * must not be drawn as north.
   */
  heading?: number;
  /** Ground speed in m/s, from the aircraft's GPS. Absent when not reported. */
  speed?: number;
  battery: {
    percentage: number | null;
    voltage: number | null;
    isHealthy: boolean;
  };
  /**
   * What this aircraft will land itself at (#629). Absent from a backend that
   * does not send it; absent is not "no threshold" — the aircraft always has
   * one, we simply have not been told which.
   */
  landingPolicy?: BatteryPolicy;
  imageUrl: string | null;
  lastUpdateTime: string; // ISO 8601
  currentMissionId: string | null;
}

/**
 * Body of `GET /api/v1/drones` — a paged collection, not a bare array.
 *
 * Owner-scoped since #382, in three buckets since #575: `drones` is the
 * signed-in operator's own fleet, `others` is aircraft owned by a *different*
 * operator — only ever populated for a caller holding `fleet:read_all` — and
 * `unclaimed` holds drones with no owner at all, registered through the factory
 * provisioning flow where no operator was present to own them.
 *
 * Separate buckets rather than hidden: an unclaimed aircraft can be airborne,
 * and one no dashboard shows is one nobody is watching. Somebody else's
 * aircraft is shown to an admin for the same reason, and labelled as theirs so
 * that responsibility stays with a named party (#564).
 *
 * `others` and `unclaimed` are optional so a UI build stays compatible with a
 * backend that predates them; `getAllDrones` merges all three so nothing drops
 * off the map.
 */
export interface DroneListResponse {
  drones: ApiDrone[];
  others?: ApiDrone[];
  unclaimed?: ApiDrone[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * A drone exactly as `GET /api/v1/drones` sends it — snake_case, and a
 * different shape from {@link Drone} (#508).
 *
 * This type exists because pretending the two are the same is what broke the
 * fleet map. The server's `models.Drone` carries `last_known_position`,
 * `battery_percent` and a `status` enum that includes `provisioning` and
 * `flying`; it carries no `lastUpdateTime`, and a `name` only once someone has
 * given the drone one (#406).
 * Heading and ground speed ride on `last_known_position` (#711).
 *
 * Every field is optional. Not defensiveness — `omitempty` on the Go structs
 * means the server really does leave them out, and a type that claims otherwise
 * is a type that lies to its consumers.
 *
 * @see droneService — the single place this is converted to {@link Drone}.
 */
export interface ApiDrone {
  /** Flight commands this drone's state admits; absent from an older backend. */
  available_commands?: string[];
  id: string;
  hardware_id?: string;
  /** 1–64 characters; omitted when nobody has named the drone (#406). */
  name?: string;
  status?: string;
  state?: string;
  battery_percent?: number;
  last_known_position?: {
    latitude: number;
    longitude: number;
    altitude?: number;
    /** Degrees from north, 0 to 359.99. Omitted when not reported (#711). */
    heading?: number;
    /** m/s over the ground. Omitted when not reported (#711). */
    ground_speed?: number;
  };
  device_model?: string;
  firmware_version?: string;
  /** The board's software build, as it last reported it (#791). */
  software_version?: string;
  created_at?: string;
  last_heartbeat?: string;
  geofence_id?: string;
  virtual?: boolean;
  /** The drone's credentials state (#777); absent when never issued. */
  credentials?: { state: CredentialsState; expires_at: string };
  metadata?: {
    owner_id?: string;
    deployment_region?: string;
  };
  /**
   * The effective low-battery landing policy (#629). Computed per response by
   * the server, so it is present on every drone — including one nobody has
   * configured, where `threshold` is the fleet default and `is_default` is true.
   *
   * Optional here only because an older backend does not send it, the same
   * reasoning as `available_commands`.
   */
  battery_policy?: ApiBatteryPolicy;
}

/**
 * The battery policy exactly as the server sends it — snake_case, and the body of
 * `GET`/`PUT /api/v1/drones/{droneID}/battery` as well as the `battery_policy`
 * field on a drone (#629).
 *
 * Declared once and used in both places, because they are the same object: a
 * second declaration for the endpoint is how the two would come to disagree about
 * which keys are optional.
 */
export interface ApiBatteryPolicy {
  pack?: BatteryPack;
  threshold: { volts_per_cell: number; dwell_seconds: number };
  is_default: boolean;
  threshold_percent?: number;
  floor_volts_per_cell: number;
  ceiling_volts_per_cell: number;
}

/**
 * The battery pack an operator recorded for a drone (#629).
 *
 * Every field present or the whole object absent: the server will not store a
 * cell count without a chemistry, because a voltage cannot be interpreted
 * without one.
 */
export interface BatteryPack {
  cells: number;
  chemistry: 'lipo' | 'li-ion';
  capacity_mah: number;
}

/**
 * The low-battery landing policy a drone is actually flying (#629).
 *
 * Named `battery_policy` on the wire, and deliberately not `battery`: the domain
 * {@link Drone} already has a `battery` — the live reading this adapter
 * assembles — and one key meaning two things either side of a conversion is what
 * #508 was.
 *
 * `threshold` is never absent. There is no state in which an aircraft has no
 * threshold: unset means the fleet default, which `isDefault` reports.
 *
 * `pack` and `thresholdPercent` are both optional, and for different reasons.
 * `pack` is absent when nobody has described the battery. `thresholdPercent` is
 * absent when the pack's chemistry has no measured discharge curve — a
 * percentage derived from the wrong curve would be precise and wrong, so the
 * server sends volts and no percentage rather than a number it cannot stand
 * behind. **Render the absence; do not fall back to a computed figure.**
 */
export interface BatteryPolicy {
  pack?: BatteryPack;
  thresholdVoltsPerCell: number;
  thresholdDwellSeconds: number;
  isDefault: boolean;
  thresholdPercent?: number;
  /**
   * The range this drone's threshold may be set to.
   *
   * Served by the API rather than hard-coded here (#578's rule: the browser does
   * not decide, it explains). The floor is provisional and expected to move once
   * there is recorded current-draw data, so a copy in this file would be the
   * fifth place one rule is written down — which is the whole of #629.
   */
  floorVoltsPerCell: number;
  ceilingVoltsPerCell: number;
}

/** What one live telemetry frame changes about a drone already on the map. */
export interface PositionUpdate {
  droneId: string;
  position: NonNullable<Drone['position']>;
  heading: Drone['heading'];
  speed: Drone['speed'];
  battery: Drone['battery'];
  lastUpdateTime: string;
  /** Absent from an older backend, which did not send one: keep what it had. */
  status?: Drone['status'];
}

/**
 * A drone's telemetry was accepted: where it now is (#710).
 *
 * The shape is the server's (`handlers.DronePositionEvent`), recorded from a
 * real frame into `tests/fixtures/drone-position-update.contract.json`. The
 * type this replaces was written from the UI's side, with camelCase keys,
 * a heading, a speed and a status; the server had never sent any of it, and
 * had never sent the event at all. Heading and ground speed now ride on the
 * position, under the server's names (#711), and the status beside it since a
 * frame sets it (#787).
 *
 * The keys mean what they mean in `GET /drones` ({@link ApiDrone}), because
 * the server reads them back from the same stored record.
 */
export interface DronePositionUpdate {
  type: 'drone_position_update';
  data: {
    drone_id: string;
    last_known_position: {
      latitude: number;
      longitude: number;
      altitude: number;
      /** Omitted when the aircraft did not report one. Not north. */
      heading?: number;
      ground_speed?: number;
      timestamp: string;
    };
    battery_percent?: number;
    last_heartbeat: string;
    /** The server's status vocabulary; absent from a backend before #787. */
    status?: string;
  };
  timestamp?: string;
}

/** Whether a drone can still refresh its tokens (#777). */
export type CredentialsState = 'valid' | 'expired' | 'revoked';

/** A drone's credentials: valid until expiresAt, or no longer working. */
export interface DroneCredentials {
  state: CredentialsState;
  /** ISO 8601. With rotation, each refresh moves it on: 180 days physical, 30 virtual (#808). */
  expiresAt: string;
}
